import PageHeader from "../components/PageHeader";
import { useLang } from "../i18n/LanguageProvider";

const PHILOSOPHY_IMG =
  "https://images.unsplash.com/photo-1531591022136-eb8b0da1e6d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWxpc3QlMjBjdXJ2ZXN8ZW58MHx8fHwxNzg0MDY1MDY1fDA&ixlib=rb-4.1.0&q=85";

const principles = [
  {
    n: "01",
    title: "Capital is not a game.",
    body: "We treat every allocation as a decision that outlives the decision-maker. Volatility is a byproduct; permanent loss is the enemy we truly manage against."
  },
  {
    n: "02",
    title: "Owners, not renters.",
    body: "Wherever practical, we co-invest as principals — in operating businesses, in real assets, in private strategies where alignment can be documented, not implied."
  },
  {
    n: "03",
    title: "The best investment is one that lets you sleep.",
    body: "Liquidity, leverage and concentration are managed to a threshold each family can defend — emotionally as much as mathematically — through any cycle."
  },
  {
    n: "04",
    title: "Fees compound. So does trust.",
    body: "We negotiate institutional-grade terms across managers. We never accept commissions, rebates or retrocessions. Every dollar of cost is disclosed and defended."
  },
  {
    n: "05",
    title: "Time is the alpha.",
    body: "Our advantage is not information, model or speed. It is the mandate to hold — to wait years for the right compounder, and to stay through the storm that others cannot."
  },
  {
    n: "06",
    title: "Nothing is bought that cannot be explained to the heirs.",
    body: "Complexity is only tolerated when it earns its keep. Portfolios are constructed to be intelligible across generations, not clever for the moment."
  }
];

export default function Philosophy() {
  const { t } = useLang();
  return (
    <>
      <PageHeader
        eyebrow={t("page.philosophy.eyebrow")}
        title={t("page.philosophy.title")}
        lede={t("page.philosophy.lede")}
      />

      <section className="relative pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="relative aspect-[21/9] overflow-hidden border border-[var(--bw-border)]">
            <img
              src={PHILOSOPHY_IMG}
              alt="Abstract architectural curves"
              className="w-full h-full object-cover"
              style={{ filter: "brightness(0.5) grayscale(0.2)" }}
            />
            <div className="absolute inset-0 flex items-end p-8 lg:p-14">
              <div className="max-w-2xl">
                <div className="eyebrow mb-4">Doctrine</div>
                <div className="font-serif-display text-white text-2xl lg:text-3xl leading-[1.25]">
                  &ldquo;Return without a framework is <span className="text-gold italic">luck.</span> Framework without return is <span className="text-gold italic">theatre.</span> We reject both.&rdquo;
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {principles.map((p, i) => (
              <div
                key={p.n}
                className={`card-lux lg:col-span-6 ${i % 2 === 1 ? "lg:translate-y-8" : ""}`}
                data-testid={`principle-${p.n}`}
              >
                <div className="flex items-start gap-8">
                  <div className="font-serif-display text-4xl text-gold">{p.n}</div>
                  <div>
                    <h3 className="font-serif-display text-2xl text-white leading-tight mb-3">{p.title}</h3>
                    <p className="text-muted text-sm leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--bw-border)] py-24 bg-surface">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Allocation Framework</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-white leading-tight">
              A portfolio built around <span className="text-gold italic">three concentric circles.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {[
              { t: "Reserve", pct: "25–35%", d: "Liquid, defensive, currency-diversified — years of family expenditure held in the safest instruments in the world." },
              { t: "Core", pct: "40–55%", d: "Global equities and credit, held through institutional managers with decadal track records. The compounding engine." },
              { t: "Conviction", pct: "15–30%", d: "Private markets, real assets, direct co-investments and family-selected themes. Where patience is priced in." },
            ].map((r) => (
              <div key={r.t} className="border border-[var(--bw-border)] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-baseline gap-6">
                  <span className="font-serif-display text-3xl text-gold">{r.pct}</span>
                  <span className="font-serif-display text-xl text-white">{r.t}</span>
                </div>
                <p className="text-muted text-sm max-w-md leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
